import React, { useState, useCallback } from 'react'
import { 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Typography, 
    CircularProgress,
    Alert,
    Box,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Chip
} from '@mui/material'
import { 
    Sync as SyncIcon, 
    CheckCircle as SuccessIcon, 
    Error as ErrorIcon,
    Warning as WarningIcon,
    Business as BusinessIcon
} from '@mui/icons-material'
import { 
    useListContext, 
    useNotify, 
    useRefresh,
    useUnselectAll 
} from 'react-admin'
import fetchApi from '@/lib/tools/api'

interface SyncResult {
    id: number
    name: string
    success: boolean
    message?: string
}

interface BulkSyncResponse {
    success: boolean
    results: SyncResult[]
    total: number
    successful: number
    failed: number
}

export default function BulkSireneSyncButton() {
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [results, setResults] = useState<SyncResult[]>([])
    const [syncStats, setSyncStats] = useState<{ successful: number; failed: number; total: number } | null>(null)
    
    const { selectedIds, data } = useListContext()
    const notify = useNotify()
    const refresh = useRefresh()
    const unselectAll = useUnselectAll('producers')

    const selectedProducers = selectedIds.map(id => 
        data?.find((producer: any) => producer.id === id)
    ).filter(Boolean)

    const handleClickOpen = useCallback(() => {
        setOpen(true)
        setResults([])
        setSyncStats(null)
        setProgress(0)
    }, [])

    const handleClose = useCallback(() => {
        setOpen(false)
        setResults([])
        setSyncStats(null)
        setProgress(0)
        if (syncStats && syncStats.successful > 0) {
            refresh()
            unselectAll()
        }
    }, [refresh, unselectAll, syncStats])

    const handleBulkSync = useCallback(async () => {
        if (!selectedIds.length) return

        setLoading(true)
        setProgress(0)
        setResults([])
        setSyncStats(null)

        try {
            // Call the backend API to trigger bulk SIRENE sync
            const response = await fetchApi<BulkSyncResponse>(
                `/producers/bulk-sync-sirene`,
                { producer_ids: selectedIds },
                null,
                'POST'
            )

            setResults(response.results)
            setSyncStats({
                total: response.total,
                successful: response.successful,
                failed: response.failed
            })

            setProgress(100)

            if (response.successful > 0) {
                notify(
                    `${response.successful} producteur${response.successful > 1 ? 's' : ''} synchronisé${response.successful > 1 ? 's' : ''} avec succès`,
                    { type: 'success' }
                )
            }

            if (response.failed > 0) {
                notify(
                    `${response.failed} synchronisation${response.failed > 1 ? 's' : ''} échouée${response.failed > 1 ? 's' : ''}`,
                    { type: 'warning' }
                )
            }

        } catch (error: any) {
            const errorMessage = error.message || 'Erreur lors de la synchronisation en lot'
            notify(`Échec de la synchronisation: ${errorMessage}`, { type: 'error' })
            setSyncStats({
                total: selectedIds.length,
                successful: 0,
                failed: selectedIds.length
            })
        } finally {
            setLoading(false)
        }
    }, [selectedIds, notify])

    if (!selectedIds.length) {
        return null
    }

    const isCompleted = syncStats !== null
    const successfulResults = results.filter(r => r.success)
    const failedResults = results.filter(r => !r.success)

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
                onClick={handleClickOpen}
                disabled={loading}
                sx={{ 
                    ml: 1,
                    '&:hover': {
                        transform: 'scale(1.02)',
                        transition: 'transform 0.2s ease-in-out'
                    }
                }}
            >
                Synchroniser SIRENE ({selectedIds.length})
            </Button>

            <Dialog
                open={open}
                onClose={loading ? undefined : handleClose}
                maxWidth="md"
                fullWidth
                aria-labelledby="bulk-sirene-sync-dialog-title"
                disableEscapeKeyDown={loading}
            >
                <DialogTitle id="bulk-sirene-sync-dialog-title">
                    <Box display="flex" alignItems="center" gap={1}>
                        <SyncIcon color="primary" />
                        Synchronisation en lot INSEE SIRENE
                    </Box>
                </DialogTitle>
                
                <DialogContent sx={{ minHeight: '300px' }}>
                    {isCompleted ? (
                        // Results view
                        <Box>
                            <Alert 
                                severity={syncStats.failed === 0 ? "success" : syncStats.successful === 0 ? "error" : "warning"}
                                sx={{ mb: 3 }}
                            >
                                <Typography variant="body1" fontWeight="bold">
                                    Synchronisation terminée
                                </Typography>
                                <Typography variant="body2">
                                    {syncStats.successful} succès • {syncStats.failed} échecs sur {syncStats.total} producteurs
                                </Typography>
                            </Alert>

                            {successfulResults.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" color="success.main" gutterBottom>
                                        <SuccessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Synchronisations réussies ({successfulResults.length})
                                    </Typography>
                                    <List dense>
                                        {successfulResults.map((result) => (
                                            <ListItem key={result.id}>
                                                <ListItemIcon>
                                                    <SuccessIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={result.name}
                                                    secondary={result.message || "Données SIRENE mises à jour"}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {failedResults.length > 0 && (
                                <Box>
                                    <Typography variant="h6" color="error.main" gutterBottom>
                                        <ErrorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Synchronisations échouées ({failedResults.length})
                                    </Typography>
                                    <List dense>
                                        {failedResults.map((result) => (
                                            <ListItem key={result.id}>
                                                <ListItemIcon>
                                                    <ErrorIcon color="error" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={result.name}
                                                    secondary={result.message || "Erreur inconnue"}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    ) : loading ? (
                        // Loading view
                        <Box>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body1">
                                        Synchronisation en cours... ({selectedIds.length} producteurs)
                                    </Typography>
                                </Box>
                            </Alert>

                            <Box sx={{ mb: 2 }}>
                                <LinearProgress 
                                    variant="indeterminate" 
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        mb: 1
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary" align="center">
                                    Traitement des données SIRENE... Cela peut prendre plusieurs minutes.
                                </Typography>
                            </Box>

                            <Typography variant="body2" sx={{ mt: 2 }}>
                                Producteurs sélectionnés :
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {selectedProducers.slice(0, 10).map((producer: any) => (
                                    <Chip
                                        key={producer.id}
                                        icon={<BusinessIcon />}
                                        label={producer.name || `ID: ${producer.id}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                                {selectedProducers.length > 10 && (
                                    <Chip
                                        label={`... et ${selectedProducers.length - 10} autres`}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>
                    ) : (
                        // Confirmation view
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Synchroniser {selectedIds.length} producteur{selectedIds.length > 1 ? 's' : ''}
                            </Typography>
                            
                            <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
                                <Typography variant="body2">
                                    <strong>Attention :</strong> Cette opération va mettre à jour les données SIRENE 
                                    pour tous les producteurs sélectionnés. Les données existantes seront remplacées 
                                    par celles de la base INSEE officielle.
                                </Typography>
                            </Alert>

                            <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>Données qui seront mises à jour :</strong>
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, m: 0, mb: 3 }}>
                                <Typography component="li" variant="body2">
                                    Statut administratif (Actif/Cessé)
                                </Typography>
                                <Typography component="li" variant="body2">
                                    Date de cessation d'activité (si applicable)
                                </Typography>
                                <Typography component="li" variant="body2">
                                    Autres informations administratives disponibles
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="body2" gutterBottom>
                                <strong>Producteurs sélectionnés :</strong>
                            </Typography>
                            <Box sx={{ 
                                maxHeight: '200px', 
                                overflow: 'auto',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1
                            }}>
                                {selectedProducers.map((producer: any) => (
                                    <Chip
                                        key={producer.id}
                                        icon={<BusinessIcon />}
                                        label={producer.name || `ID: ${producer.id}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ m: 0.5 }}
                                    />
                                ))}
                            </Box>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Estimation :</strong> Cette opération peut prendre plusieurs minutes 
                                    selon le nombre de producteurs à traiter.
                                </Typography>
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {isCompleted ? (
                        <Button onClick={handleClose} color="primary" variant="contained">
                            Fermer
                        </Button>
                    ) : (
                        <>
                            <Button 
                                onClick={handleClose} 
                                disabled={loading}
                                color="inherit"
                            >
                                Annuler
                            </Button>
                            <Button
                                onClick={handleBulkSync}
                                disabled={loading}
                                variant="contained"
                                color="primary"
                                startIcon={loading ? <CircularProgress size={20} /> : <SyncIcon />}
                            >
                                {loading ? 'Synchronisation...' : 'Démarrer la synchronisation'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    )
}